import React from 'react';
import classNames from 'classnames';

import { defaqtoSourceData } from '@starlingbank/shared';

import * as classes from './PreviewCustomTable.module.scss';

import TextContent from '../textContent/TextContent';

import tick from '../../../content/media-library/icons/yes.svg';
import cross from '../../../content/media-library/icons/no.svg';

import starlingLogo from '../../../content/media-library/logos/tables/Starling.png';
import natwestLogo from '../../../content/media-library/logos/tables/NatWest.png';
import lloydsLogo from '../../../content/media-library/logos/tables/Lloyds.png';
import hsbcLogo from '../../../content/media-library/logos/tables/HSBC.png';
import santanderLogo from '../../../content/media-library/logos/tables/Santander.png';
import monzoLogo from '../../../content/media-library/logos/tables/Monzo.png';

// These are the accounts we're previewing, with their IDs which come from the Defaqto feed
const ACCOUNTS = {
  360131: 'starling',
  269058: 'natwest',
  5596: 'lloyds',
  212247: 'hsbc',
  261633: 'santander',
  384729: 'monzo'
};

// These are the questions/criteria we're showing the in the preview
const QUESTIONS = [
  'App Transaction History',
  'Mobile App Card Security',
  'AER interest on £1,000',
  'Arranged Overdraft Representative APR',
  'ATM World £100 Cost',
  'Faster Payments App Limit',
  'App Cheque Imaging'
];

// The question IDs shouldn't change (but have in the past), so fetch these
const fetchQuestionIds = () =>
  QUESTIONS.reduce((prev, curr) => {
    const questionObj = defaqtoSourceData.questions.find((question) => question.text === curr);
    return { ...prev, [curr]: questionObj.id };
  }, []);

// From the whole data feed, isolate just the ones we're previewing
const fetchPreviewAccounts = () =>
  defaqtoSourceData.products
    .filter((account) => Object.keys(ACCOUNTS).includes(account.id))
    .reduce((prev, curr) => {
      return { ...prev, [ACCOUNTS[curr.id]]: curr };
    }, {});

// Fetch the answer for the given criteria
const fetchAccountCriteria = (accountData, account) => {
  const findValue = (questionId) => accountData.answers.find((answer) => answer.qid === questionId).displayText;
  const questionDetails = fetchQuestionIds();

  return {
    ...account,
    appTransactionHistory: findValue(questionDetails['App Transaction History']),
    appCardSecurity: findValue(questionDetails['Mobile App Card Security']),
    aerInterest: findValue(questionDetails['AER interest on £1,000']),
    overdraftAPR: findValue(questionDetails['Arranged Overdraft Representative APR']),
    atmWorldCost: findValue(questionDetails['ATM World £100 Cost']),
    fasterPaymentLimit: findValue(questionDetails['Faster Payments App Limit']),
    chequeImaging: findValue(questionDetails['App Cheque Imaging'])
  };
};

const generateTableData = () => {
  const lookup = fetchPreviewAccounts();

  return [
    {
      acc: 'starling',
      logo: starlingLogo,
      alt: 'Starling Logo',
      name: lookup.starling.name
    },
    {
      acc: 'natwest',
      logo: natwestLogo,
      alt: 'Natwest Logo',
      name: lookup.natwest.name
    },
    {
      acc: 'lloyds',
      logo: lloydsLogo,
      alt: 'Lloyds Bank Logo',
      name: lookup.lloyds.name
    },
    {
      acc: 'hsbc',
      logo: hsbcLogo,
      alt: 'HSBC Logo',
      name: lookup.hsbc.name
    },
    {
      acc: 'santander',
      logo: santanderLogo,
      alt: 'Santander Logo',
      name: lookup.santander.name
    },
    {
      acc: 'monzo',
      logo: monzoLogo,
      alt: 'Monzo Logo',
      name: lookup.monzo.name
    }
  ].map((account) => fetchAccountCriteria(lookup[account.acc], account));
};

const PreviewCustomTable = () => {
  const accountsUsedInPreview = generateTableData();

  return (
    <>
      <div className={classes.previewCustomTableContainer}>
        <table className={classes.previewCustomTable}>
          <thead>
            <tr>
              <th className={classes.sticky}>Feature</th>
              {accountsUsedInPreview.map((account) => {
                return (
                  <th
                    key={account.name}
                    className={classNames({
                      [classes.starling]: account.name === 'Starling Bank - Current Account'
                    })}
                  >
                    <div className={classes.bankProvider}>
                      <img src={account.logo} alt={account.alt} />
                      <span>{account.name}</span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={classes.sticky}>
                <div>
                  <span>App Transaction History</span>
                </div>
              </td>
              {accountsUsedInPreview.map((account) => {
                return (
                  <td
                    key={`${account.name} : ${account.appTransactionHistory}`}
                    className={classNames({
                      [classes.starling]: account.name === 'Starling Bank - Current Account'
                    })}
                  >
                    <span>{account.appTransactionHistory}</span>
                  </td>
                );
              })}
            </tr>
            <tr>
              <td className={classes.sticky}>
                <div>
                  <span>Mobile App Card Security</span>
                </div>
              </td>
              {accountsUsedInPreview.map((account) => {
                return (
                  <td
                    key={`${account.name} : ${account.appCardSecurity}`}
                    className={classNames({
                      [classes.starling]: account.name === 'Starling Bank - Current Account'
                    })}
                  >
                    <span>{account.appCardSecurity.replace(/(\w),(\w)/g, '$1, $2')}</span>
                  </td>
                );
              })}
            </tr>
            <tr>
              <td className={classes.sticky}>
                <div>
                  <span>AER interest on £1,000</span>
                </div>
              </td>
              {accountsUsedInPreview.map((account) => {
                return (
                  <td
                    key={`${account.name} : ${account.aerInterest}`}
                    className={classNames({
                      [classes.starling]: account.name === 'Starling Bank - Current Account'
                    })}
                  >
                    <span>{account.aerInterest}</span>
                  </td>
                );
              })}
            </tr>
            <tr>
              <td className={classes.sticky}>
                <div>
                  <span>Arranged Overdraft Representative APR</span>
                </div>
              </td>
              {accountsUsedInPreview.map((account) => {
                return (
                  <td
                    key={`${account.name} : ${account.overdraftAPR}`}
                    className={classNames({
                      [classes.starling]: account.name === 'Starling Bank - Current Account'
                    })}
                  >
                    {account.overdraftAPR}
                  </td>
                );
              })}
            </tr>
            <tr>
              <td className={classes.sticky}>
                <div>
                  <span>ATM World £100 Cost</span>
                </div>
              </td>
              {accountsUsedInPreview.map((account) => {
                return (
                  <td
                    key={`${account.name} : ${account.atmWorldCost}`}
                    className={classNames({
                      [classes.starling]: account.name === 'Starling Bank - Current Account'
                    })}
                  >
                    <span>{account.atmWorldCost}</span>
                  </td>
                );
              })}
            </tr>
            <tr>
              <td className={classes.sticky}>
                <div>
                  <span>Faster Payments App Limit</span>
                </div>
              </td>
              {accountsUsedInPreview.map((account) => {
                return (
                  <td
                    key={`${account.name} : ${account.fasterPaymentLimit}`}
                    className={classNames({
                      [classes.starling]: account.name === 'Starling Bank - Current Account'
                    })}
                  >
                    <span>{account.fasterPaymentLimit}</span>
                  </td>
                );
              })}
            </tr>
            <tr>
              <td className={classes.sticky}>
                <div>
                  <span>App Cheque Imaging</span>
                </div>
              </td>
              {accountsUsedInPreview.map((account) => {
                return (
                  <td
                    key={`${account.name} : ${account.chequeImaging}`}
                    className={classNames({
                      [classes.starling]: account.name === 'Starling Bank - Current Account'
                    })}
                  >
                    {account.chequeImaging === 'yes' ? (
                      <img className={classes.icon} src={tick} alt="Includes app cheque imaging" />
                    ) : (
                      <img className={classes.icon} src={cross} alt="Does not include app cheque imaging" />
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
      <TextContent
        customPadding="no-bottom"
        colourScheme="light-grey"
        callToAction={[
          {
            text: 'Compare all banks and features',
            link: '/current-account/compare-best-bank-accounts/',
            className: 'cta-button--secondary'
          }
        ]}
      />
      <TextContent
        text={`Data supplied by Defaqto and correct as of ${defaqtoSourceData.lastUpdated}.`}
        colourScheme="light-grey"
        overflowMobile={true}
        customPadding="no-top"
      />
    </>
  );
};

export default PreviewCustomTable;
